import { Immutable } from "immer";
import React from "react";

import { GridItem } from "../../models/tempur-love-grid";
import { GridTile } from "../GridTile";
import { MasonryGrid } from "../MasonryGrid";
import { ReviewSnippetTile } from "../ReviewSnippetTile";
import { TempurLoveStoryPageTile } from "../TempurLoveStoryPageTile";

const buildTileContent = (
    item: Immutable<GridItem>,
    linkRef: React.RefObject<HTMLAnchorElement | null>,
): { ariaLabel: string; element: React.ReactNode } => {
    switch (item.type) {
        case "cms.TempurLoveStoryPage":
        case "cms.CustomerContentHubStoryPage":
            return {
                ariaLabel: item.value.tile_title,
                element: <TempurLoveStoryPageTile data={item} ref={linkRef} />,
            };
        case "cms.TempurLoveReviewSnippetPage":
        case "cms.CustomerContentHubReviewSnippetPage":
            return {
                ariaLabel: item.value.title,
                element: <ReviewSnippetTile data={item} ref={linkRef} />,
            };
        default: {
            const _exhaustive: never = item;
            throw new Error(
                `Unhandled tile type: ${JSON.stringify(_exhaustive)}`,
            );
        }
    }
};

type IProps = Immutable<{
    data: GridItem[];
    appendItems?: boolean;
    useCappedWidth?: boolean;
}>;

export const Grid = (props: IProps): React.ReactNode => {
    const useCappedWidth = props.useCappedWidth ?? true;
    const primaryLinkRefs = React.useRef<
        Map<number, React.RefObject<HTMLAnchorElement | null>>
    >(new Map());

    const getOrCreateLinkRef = (
        item: Immutable<GridItem>,
    ): React.RefObject<HTMLAnchorElement | null> => {
        let ref = primaryLinkRefs.current.get(item.id);
        if (ref) {
            return ref;
        }
        ref = React.createRef<HTMLAnchorElement>();
        primaryLinkRefs.current.set(item.id, ref);
        return ref;
    };

    return (
        <div className="l-full-width">
            <div className={useCappedWidth ? "l-capped-width" : undefined}>
                <article className="posts">
                    <MasonryGrid
                        appendItems={props.appendItems}
                        className={"posts__container"}
                        itemSelector={".post"}
                        entities={props.data}
                        getTileID={(item) => `${item.id}`}
                        buildTile={(item, tileRef) => {
                            const linkRef = getOrCreateLinkRef(item);
                            const { ariaLabel, element } = buildTileContent(
                                item,
                                linkRef,
                            );
                            return (
                                <GridTile
                                    key={item.id}
                                    forwardedRef={tileRef}
                                    primaryLinkRef={linkRef}
                                    tileUID={item.id}
                                    ariaLabel={ariaLabel}
                                >
                                    {element}
                                </GridTile>
                            );
                        }}
                        emptyState={null}
                        options={{
                            horizontalOrder: true,
                            gutter: 10,
                            transitionDuration: 0, // Disable transitions to prevent jank during layout
                        }}
                    />
                </article>
            </div>
        </div>
    );
};
